import React, { useState, useEffect, useCallback } from "react";
import "./Payment.css";
import { websitePlanPaymentService } from "service/websitePlanPaymentService";

const Payment = () => {
    const [due, setDue] = useState(null);       // full breakdown from API
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState("wallet");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [successInfo, setSuccessInfo] = useState(null);

    const loadDue = useCallback(async () => {
        setLoading(true);
        setApiError(null);
        try {
            const res = await websitePlanPaymentService.getDue();
            setDue(res.data);
        } catch (err) {
            setApiError(err.message || "Failed to load plan dues");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDue();
    }, [loadDue]);

    const handlePayNow = async () => {
        setIsSubmitting(true);
        setApiError(null);
        try {
            const res = await websitePlanPaymentService.pay();
            setSuccessInfo(res.data);
            setPaymentSuccess(true);
            setShowModal(false);
        } catch (err) {
            setApiError(err.message || "Payment failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setPaymentSuccess(false);
        setSuccessInfo(null);
        loadDue();
    };
     
    const totalAmount = due?.total_payable ? Number(due.total_payable) : 0;

    return (
        <div className="opay-payment-container mt-0">
            {/* Top Header Filter Bar */}
            <div className="opay-payment-header-bar mb-3">
                <div className="opay-payment-left">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opay-payment-home-icon me-1" style={{ verticalAlign: "middle" }}>
                        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    <label className="opay-payment-breadcrumb-label ms-2">
                        <input type="checkbox" className="opay-payment-checkbox" defaultChecked />
                        <span>WebSite Payments</span>
                    </label>
                </div>
            </div>

            {apiError && (
                <div className="alert alert-danger" role="alert">
                    {apiError}
                </div>
            )}

            {loading ? (
                <div className="text-center py-5 text-muted">Loading plan dues…</div>
            ) : paymentSuccess ? (
                <div className="opay-payment-success-card text-center py-5">
                    <div className="success-icon-wrapper mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-success mb-3">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </div>
                    <h2 className="fw-bold text-dark">Payment Successful!</h2>
                    <p className="text-muted max-width-500 mx-auto mt-2">
                        ₹{successInfo?.amount_paid} was deducted from your wallet. Your plan is renewed until{" "}
                        <strong>{successInfo?.renewal_date}</strong>.
                    </p>
                    <div className="mt-4">
                        <button className="btn btn-primary px-4 py-2 me-2" onClick={handleReset}>
                            Refresh
                        </button>
                        <a href="/paymenthistory" className="btn btn-outline-dark px-4 py-2">
                            View Payment History
                        </a>
                    </div>
                </div>
            ) : !due ? (
                <div className="opay-payment-table-card text-center py-5 text-muted">
                    No website plan is assigned to your account yet. Please contact your Main Admin.
                </div>
            ) : (
                <div className="opay-payment-table-card">
                    {/* Info Section above Table */}
                    <div className="opay-payment-info-row">
                        <span>Plan Activated : {due.plan_activation_date || "—"}</span>
                        <span>
                            {due.is_overdue ? "Overdue since" : "Valid Till"} : {due.valid_till || "—"}
                        </span>
                    </div>

                    {/* Table displaying the Website Payment details */}
                    <div className="table-responsive">
                        <table className="table opay-payment-table">
                            <thead>
                                <tr>
                                    <th className="text-center" style={{ width: "40px" }}>
                                        <input type="checkbox" className="custom-table-checkbox" defaultChecked />
                                    </th>
                                    <th className="text-start">Info</th>
                                    <th>Plan Name</th>
                                    <th>Cycle</th>
                                    <th>Plan Payment</th>
                                    <th>Sleep Days</th>
                                    <th>Sleep Penalty</th>
                                    <th>Total</th>
                                    <th>Gst</th>
                                    <th>Total Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="text-center">
                                        <input type="checkbox" className="custom-table-checkbox" defaultChecked />
                                    </td>
                                    <td className="text-start">
                                        WebSite Payment(Server + Maintenance + Support) Charges
                                    </td>
                                    <td>
                                        <span className="opay-badge opay-badge-purple">{due.plan_name}</span>
                                    </td>
                                    <td>30 Days</td>
                                    <td>₹{due.price}</td>
                                    <td>{due.sleep_days}</td>
                                    <td>₹{due.sleep_penalty}</td>
                                    <td>₹{(Number(due.price) + Number(due.sleep_penalty)).toFixed(2)}</td>
                                    <td>₹{due.gst}</td>
                                    <td></td>
                                </tr>
                                <tr className="opay-total-row">
                                    <td className="text-center"></td>
                                    <td className="text-start">Total</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td>
                                        <button className="opay-pay-btn" onClick={() => setShowModal(true)}>
                                            pay ₹{totalAmount.toFixed(2)}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="opay-payment-info-row mt-2">
                        <span>Wallet Balance : ₹{due.wallet_balance}</span>
                        {due.is_overdue && (
                            <span className="text-danger fw-bold">
                                Your plan is overdue — your downline (Retailer/Distributor) cannot log in until this is paid.
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showModal && due && (
                <div className="opay-modal-overlay">
                    <div className="opay-modal-content">
                        <button className="opay-modal-close-btn" onClick={() => setShowModal(false)}>
                            &times;
                        </button>
                        <h4 className="fw-bold mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2" style={{ verticalAlign: "middle" }}>
                                <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                                <path d="M3 10h18v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
                                <path d="M16 16h2" />
                            </svg> Confirm Payment
                        </h4>

                        <div className="opay-tab-content text-center py-4">
                            <p className="text-muted mb-2">
                                ₹{totalAmount.toFixed(2)} will be deducted directly from your Main Wallet.
                            </p>
                            <p className="fw-bold mb-4">Wallet Balance: ₹{due.wallet_balance}</p>

                            {apiError && <div className="alert alert-danger py-2">{apiError}</div>}

                            <button
                                className="btn btn-success btn-lg w-100 fw-semibold"
                                disabled={isSubmitting || Number(due.wallet_balance) < totalAmount}
                                onClick={handlePayNow}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span> Processing…
                                    </>
                                ) : (
                                    `Pay Now (₹ ${totalAmount.toFixed(2)})`
                                )}
                            </button>

                            {Number(due.wallet_balance) < totalAmount && (
                                <p className="text-danger mt-3 mb-0">
                                    Insufficient wallet balance. Please load your wallet first.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payment;